import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {getEstimateFollowUps, stopEstimateFollowUps} from '../estimate.service';
import {isAuthRedirectError} from '../../../shared/services/auth.service';
import {logError} from '../../../shared/services/logger.service';
import {SimpleDialog} from './EstimateFooter';
import './InvoiceRecord.css';

const FOLLOW_UPS_OFF_LINE = 'Automatic follow-ups are turned off, so none go out on any estimate.';

/**
 * SQ-745 (Android's EstimateFollowUpsLine, SQ-563): under Send Estimate on a
 * Pending Approval estimate. Before a stop, a small button (asked first);
 * after it, a line saying so. Sending again starts them (the server clears
 * the stop), so sentTick re-reads after every send. The stop is real the
 * moment it is confirmed: there is no Save here. If the read fails, nothing
 * shows rather than a guess.
 */
export const EstimateFollowUpsLine = ({estimateId, sentTick, canStop, working}: {
  estimateId: number; sentTick: number;
  /** Holds estimates.send: who may send the estimate may stop its follow-ups. */
  canStop: boolean; working: boolean;
}) => {
  const navigate = useNavigate();
  const [stopped, setStopped] = useState<boolean>();
  // SQ-745: the company's switch (or schedule) sends none at all: said so in place of Stop, as on an invoice.
  const [companyOff, setCompanyOff] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [confirm, setConfirm] = useState(false);

  useEffect(() => {
    let live = true;
    setError(undefined);
    getEstimateFollowUps(navigate, estimateId)
      .then(answer => {
        if (!live) return;
        setStopped(answer.stoppedAt != null);
        setCompanyOff(answer.companyFollowUpsEnabled === false);
      })
      .catch(e => {
        if (!live || isAuthRedirectError(e)) return;
        setStopped(undefined);
        logError('Error reading estimate follow-ups', {estimateId}, e as Error);
      });
    return () => { live = false; };
  }, [navigate, estimateId, sentTick]);

  if (stopped === undefined) return null;
  if (companyOff) {
    return (
      <div className="iv-follow-ups" role="group" aria-label="Automatic follow-ups">
        <div className="iv-follow-ups-text">{FOLLOW_UPS_OFF_LINE}</div>
      </div>
    );
  }

  const stop = async () => {
    if (busy) return;
    setBusy(true);
    setError(undefined);
    try {
      const answer = await stopEstimateFollowUps(navigate, estimateId);
      setStopped(answer.stoppedAt != null);
    } catch (e) {
      if (isAuthRedirectError(e)) return;
      logError('Error stopping estimate follow-ups', {estimateId}, e as Error);
      setError("Couldn't stop the follow-ups. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="iv-follow-ups" role="group" aria-label="Automatic follow-ups">
      {stopped
        ? <div className="iv-follow-ups-text">No follow-ups on this estimate. Resend it to start them again.</div>
        : canStop && (
          <button type="button" className="er-tonal er-tonal--compact" disabled={working || busy} onClick={() => setConfirm(true)}>
            Stop automatic follow-ups
          </button>
        )}
      {error && <div className="er-small er-error">{error}</div>}
      {confirm && (
        <SimpleDialog title="Stop follow-ups on this estimate?"
                      text="No more follow-ups will go out on this estimate. Resending it starts them again."
                      confirm="Stop follow-ups" cancel="Keep them" danger
                      onConfirm={() => void stop()} onClose={() => setConfirm(false)}/>
      )}
    </div>
  );
};
